/**
 * Search Page
 * Date and guest selection form
 */

import { t, formatDate, getLanguage } from '../i18n';
import { Header, Card, StepIndicator, HotelCard, renderServiceTabs } from '../components';
import { getState, setSearch, setAvailableRooms, navigateTo, setLoading, setError } from '../store';
import { searchRooms, getHotelInfo, fetchActiveServicesConfig } from '../api';
import { MAX_ADULTS, MAX_CHILDREN, MAX_ROOMS } from '../config';
import type { SearchCriteria } from '../types';
import Litepicker from 'litepicker';
import 'litepicker/dist/plugins/mobilefriendly';

/**
 * Get tomorrow's date as default check-in
 */
function getDefaultCheckIn(): string {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
}

/**
 * Get day after tomorrow as default check-out
 */
function getDefaultCheckOut(): string {
  const date = new Date();
  date.setDate(date.getDate() + 2);
  return date.toISOString().split('T')[0];
}

/**
 * Render search page
 */
export function renderSearchPage(): string {
  const state = getState();
  const search = state.search || {
    checkIn: getDefaultCheckIn(),
    checkOut: getDefaultCheckOut(),
    adults: 2,
    children: 0,
    rooms: 1,
  };

  // Format dates for display
  const checkInFormatted = formatDate(search.checkIn);
  const checkOutFormatted = formatDate(search.checkOut);

  // HotelCard will show loading state initially, then updated by initSearchPage
  return `
    ${Header(t('searchTitle'))}
    ${renderServiceTabs('search')}

    ${StepIndicator(1)}
    
    <div class="page-content">
      <div id="hotel-card-container">${HotelCard()}</div>
      
      <form id="search-form" class="search-form">
        ${Card(`
          <div class="date-section-vertical">
            <div class="form-group date-range-group">
              <label class="form-label">📅 ${t('checkIn')} / ${t('checkOut')}</label>
              <div class="date-range-display" id="date-range-trigger">
                <div class="date-range-value">
                  <span class="date-label">${t('checkIn')}</span>
                  <span class="date-value" id="checkIn-display">${checkInFormatted}</span>
                </div>
                <span class="date-arrow">→</span>
                <div class="date-range-value">
                  <span class="date-label">${t('checkOut')}</span>
                  <span class="date-value" id="checkOut-display">${checkOutFormatted}</span>
                </div>
                <span class="date-toggle-icon">▼</span>
              </div>
              <!-- Hidden inputs for form submission -->
              <input type="hidden" id="checkIn" name="checkIn" value="${search.checkIn}" required />
              <input type="hidden" id="checkOut" name="checkOut" value="${search.checkOut}" required />
            </div>
            
            <!-- Calendar container for inline display (collapsed by default) -->
            <div id="litepicker-container" class="litepicker-inline collapsed"></div>
          </div>
        `)}
        
        ${Card(`
          <div class="guest-section">
            <div class="guest-row">
              <div class="guest-label">
                <span class="icon">👤</span>
                ${t('adults')}
              </div>
              <div class="counter">
                <button type="button" class="counter-btn" onclick="window.decrementCounter('adults')">−</button>
                <input type="number" id="adults" name="adults" value="${search.adults}" min="1" max="${MAX_ADULTS}" readonly />
                <button type="button" class="counter-btn" onclick="window.incrementCounter('adults')">+</button>
              </div>
            </div>
            
            <div class="guest-row">
              <div class="guest-label">
                <span class="icon">👶</span>
                ${t('children')}
              </div>
              <div class="counter">
                <button type="button" class="counter-btn" onclick="window.decrementCounter('children')">−</button>
                <input type="number" id="children" name="children" value="${search.children}" min="0" max="${MAX_CHILDREN}" readonly />
                <button type="button" class="counter-btn" onclick="window.incrementCounter('children')">+</button>
              </div>
            </div>
            
            <div class="guest-row">
              <div class="guest-label">
                <span class="icon">🛏️</span>
                ${t('rooms')}
              </div>
              <div class="counter">
                <button type="button" class="counter-btn" onclick="window.decrementCounter('rooms')">−</button>
                <input type="number" id="rooms" name="rooms" value="${search.rooms}" min="1" max="${MAX_ROOMS}" readonly />
                <button type="button" class="counter-btn" onclick="window.incrementCounter('rooms')">+</button>
              </div>
            </div>
          </div>
        `)}
        
        <div class="form-actions">
          <button type="submit" class="btn btn-primary btn-block btn-lg">
            🔍 ${t('searchButton')}
          </button>
        </div>
      </form>
    </div>
  `;
}

/**
 * Update max adults/children based on rooms count
 * Rule: max guests = BASE_MAX × rooms
 */
function updateGuestLimits(): void {
  const roomsInput = document.getElementById('rooms') as HTMLInputElement;
  const adultsInput = document.getElementById('adults') as HTMLInputElement;
  const childrenInput = document.getElementById('children') as HTMLInputElement;

  if (!roomsInput || !adultsInput || !childrenInput) return;

  const rooms = parseInt(roomsInput.value) || 1;

  // Base max per room (could come from hotel config later)
  const BASE_MAX_ADULTS_PER_ROOM = 4;
  const BASE_MAX_CHILDREN_PER_ROOM = 3;

  // Calculate new max based on rooms
  const newMaxAdults = Math.min(rooms * BASE_MAX_ADULTS_PER_ROOM, MAX_ADULTS);
  const newMaxChildren = Math.min(rooms * BASE_MAX_CHILDREN_PER_ROOM, MAX_CHILDREN);

  // Update max attributes
  adultsInput.max = newMaxAdults.toString();
  childrenInput.max = newMaxChildren.toString();

  // Clamp current values if they exceed new max
  const currentAdults = parseInt(adultsInput.value);
  const currentChildren = parseInt(childrenInput.value);

  if (currentAdults > newMaxAdults) {
    adultsInput.value = newMaxAdults.toString();
  }
  if (currentChildren > newMaxChildren) {
    childrenInput.value = newMaxChildren.toString();
  }

  // Ensure at least 1 adult per room
  const minAdults = rooms;
  adultsInput.min = minAdults.toString();
  if (currentAdults < minAdults) {
    adultsInput.value = minAdults.toString();
  }
}

/**
 * Initialize search page event handlers
 */
export async function initSearchPage(): Promise<void> {
  const form = document.getElementById('search-form');
  const checkInInput = document.getElementById('checkIn') as HTMLInputElement;
  const checkOutInput = document.getElementById('checkOut') as HTMLInputElement;
  const checkInDisplay = document.getElementById('checkIn-display');
  const checkOutDisplay = document.getElementById('checkOut-display');
  const pickerContainer = document.getElementById('litepicker-container');

  // Check if we're in edit mode
  const urlParams = new URLSearchParams(window.location.search);
  const editBookingId = urlParams.get('editBooking');

  if (editBookingId) {
    console.log('[Search] Edit mode detected, booking ID:', editBookingId);
    await loadBookingForEdit(editBookingId);
  }

  // Load hotel info from API and update the card
  loadHotelInfo();

  // Initial guest limits update
  updateGuestLimits();

  // Initialize Litepicker for date range selection
  if (pickerContainer && checkInInput && checkOutInput) {
    // Toggle calendar visibility on date-range-display click
    const dateRangeTrigger = document.getElementById('date-range-trigger');
    if (dateRangeTrigger) {
      dateRangeTrigger.addEventListener('click', () => {
        pickerContainer.classList.toggle('collapsed');
        dateRangeTrigger.classList.toggle('expanded');
      });
    }

    const picker = new Litepicker({
      element: pickerContainer,
      inlineMode: true,
      singleMode: false,
      numberOfMonths: 1,
      numberOfColumns: 1,
      minDate: new Date(),
      minDays: 1,
      startDate: checkInInput.value,
      endDate: checkOutInput.value,
      format: 'YYYY-MM-DD',
      lang: getLanguage() === 'th' ? 'th-TH' : getLanguage() === 'zh' ? 'zh-CN' : 'en-US',
      buttonText: {
        previousMonth: '‹',
        nextMonth: '›',
        apply: 'Apply',
        cancel: 'Cancel',
        reset: 'Reset',
      },
      tooltipText: {
        one: 'night',
        other: 'nights',
      },
      plugins: ['mobilefriendly'],
      mobileFriendly: true,
      setup: picker => {
        picker.on('selected', (start, end) => {
          if (start && end) {
            const startDate = start.format('YYYY-MM-DD');
            const endDate = end.format('YYYY-MM-DD');

            // Update hidden inputs
            checkInInput.value = startDate;
            checkOutInput.value = endDate;

            // Update display
            if (checkInDisplay) checkInDisplay.textContent = formatDate(startDate);
            if (checkOutDisplay) checkOutDisplay.textContent = formatDate(endDate);

            // Auto-hide calendar after selection complete
            setTimeout(() => {
              pickerContainer.classList.add('collapsed');
              const dateRangeTrigger = document.getElementById('date-range-trigger');
              if (dateRangeTrigger) dateRangeTrigger.classList.remove('expanded');
            }, 300);
          }
        });
      },
    });

    // Store picker instance for later access (e.g., edit mode)
    (window as any).__litepicker = picker;
  }

  // Counter functions
  window.incrementCounter = (name: string) => {
    const input = document.getElementById(name) as HTMLInputElement;
    const max = parseInt(input.max);
    const current = parseInt(input.value);
    if (current < max) {
      input.value = (current + 1).toString();
      // If rooms changed, update guest limits
      if (name === 'rooms') {
        updateGuestLimits();
      }
    }
  };

  window.decrementCounter = (name: string) => {
    const input = document.getElementById(name) as HTMLInputElement;
    const min = parseInt(input.min);
    const current = parseInt(input.value);
    if (current > min) {
      input.value = (current - 1).toString();
      // If rooms changed, update guest limits
      if (name === 'rooms') {
        updateGuestLimits();
      }
    }
  };

  // Form submission
  form?.addEventListener('submit', async e => {
    e.preventDefault();

    const formData = new FormData(form as HTMLFormElement);
    const search: SearchCriteria = {
      checkIn: formData.get('checkIn') as string,
      checkOut: formData.get('checkOut') as string,
      adults: parseInt(formData.get('adults') as string),
      children: parseInt(formData.get('children') as string),
      rooms: parseInt(formData.get('rooms') as string),
    };

    // Validate dates
    if (new Date(search.checkOut) <= new Date(search.checkIn)) {
      setError(t('selectDates'));
      return;
    }

    setSearch(search);
    setLoading(true);

    try {
      // Debug: Show API base URL
      const { getApiBaseUrl, getTenantId } = await import('../config');
      const apiBase = getApiBaseUrl();
      const tenantId = getTenantId();
      console.log('[Search] API Base:', apiBase, '| Tenant:', tenantId);
      console.log('[Search] Current hostname:', window.location.hostname);

      const rooms = await searchRooms(search);
      setAvailableRooms(rooms);
      navigateTo('rooms');
    } catch (error) {
      console.error('Search failed:', error);

      // Show error details on mobile
      const { getApiBaseUrl, getTenantId } = await import('../config');
      const apiBase = getApiBaseUrl();
      const tenantId = getTenantId();
      const errorMsg = error instanceof Error ? error.message : String(error);
      alert(
        `❌ เกิดข้อผิดพลาด\n\nAPI: ${apiBase}\nHost: ${window.location.hostname}\nTenant: ${tenantId}\n\nError: ${errorMsg}`
      );

      setError(t('errorNetwork'));
    } finally {
      setLoading(false);
    }
  });

  // Expose forward action to footer: submit the search form
  window.handleForward = () => {
    const formEl = document.getElementById('search-form') as HTMLFormElement | null;
    if (formEl) {
      if (typeof formEl.requestSubmit === 'function') {
        formEl.requestSubmit();
      } else {
        const btn = formEl.querySelector('button[type=submit]') as HTMLButtonElement | null;
        if (btn) btn.click();
      }
    }
  };
}

/**
 * Load hotel info from API and update HotelCard
 */
async function loadHotelInfo(): Promise<void> {
  const container = document.getElementById('hotel-card-container');

  try {
    const [hotelInfo] = await Promise.all([
      getHotelInfo(),
      fetchActiveServicesConfig(),
    ]);

    if (hotelInfo && container) {
      container.innerHTML = HotelCard({
        name: hotelInfo.hotelName,
        address: hotelInfo.hotelAddress,
        rating: hotelInfo.hotelRating,
        image: hotelInfo.hotelImage,
      });
    }
  } catch (error) {
    console.error('[Search] Failed to load hotel info:', error);
  }
}

/**
 * Load booking data for editing
 */
async function loadBookingForEdit(bookingId: string): Promise<void> {
  try {
    const { getTenantId } = await import('../config');
    const { apiFetch } = await import('../api');
    const tenantId = getTenantId();

    const result = await apiFetch<{ success: boolean; data: any }>(
      `/api/${tenantId}/bookings/${bookingId}`
    );

    if (!result.success || !result.data) {
      alert(t('errorLoadingBooking'));
      return;
    }

    const booking = result.data;
    console.log('[Search] Loaded booking for edit:', booking);

    // Store booking ID in sessionStorage for later use
    sessionStorage.setItem('editBookingId', bookingId);
    sessionStorage.setItem('editBookingData', JSON.stringify(booking));

    // Pre-fill form fields
    const checkInInput = document.getElementById('checkIn') as HTMLInputElement;
    const checkOutInput = document.getElementById('checkOut') as HTMLInputElement;
    const adultsInput = document.getElementById('adults') as HTMLInputElement;
    const childrenInput = document.getElementById('children') as HTMLInputElement;
    const roomsInput = document.getElementById('rooms') as HTMLInputElement;

    if (checkInInput) checkInInput.value = booking.checkIn.split('T')[0];
    if (checkOutInput) checkOutInput.value = booking.checkOut.split('T')[0];
    if (adultsInput) adultsInput.value = booking.adults.toString();
    if (childrenInput) childrenInput.value = booking.children.toString();
    if (roomsInput) roomsInput.value = booking.rooms.toString();

    // Update Litepicker date range
    const picker = (window as any).__litepicker;
    if (picker) {
      picker.setDateRange(booking.checkIn.split('T')[0], booking.checkOut.split('T')[0]);
    }

    // Update date display
    const checkInDisplay = document.getElementById('checkIn-display');
    const checkOutDisplay = document.getElementById('checkOut-display');
    if (checkInDisplay) checkInDisplay.textContent = formatDate(booking.checkIn.split('T')[0]);
    if (checkOutDisplay) checkOutDisplay.textContent = formatDate(booking.checkOut.split('T')[0]);

    // Update guest limits based on rooms
    updateGuestLimits();
  } catch (error) {
    console.error('[Search] Error loading booking for edit:', error);
    alert(t('errorNetwork'));
  }
}

// Extend window for global handlers
declare global {
  interface Window {
    incrementCounter: (name: string) => void;
    decrementCounter: (name: string) => void;
    handleForward?: () => void;
  }
}
